<template>
  <BlokkliProvider v-if="node && node.blokkliProps" v-bind="node.blokkliProps">
    <main v-if="node">
      <div>
        <NuxtPageDependency>
          <Breadcrumb :links="breadcrumb" class="!pt-0" />
        </NuxtPageDependency>
        <Container>
          <h1 class="text-4xl lg:text-7xl pb-5 lg:pb-12 hyphens-none">
            {{ node.title }}
          </h1>
        </Container>
        <div v-if="node.lead" class="container">
          <div class="pb-10">
            <div class="text-xl md:text-2xl" v-html="node.lead" />
          </div>
        </div>
        <div class="container">
          <form @submit.prevent>
            <SearchInput id="page-search-input" v-model="searchTerm" />
          </form>
        </div>
        <Container v-if="data && searchTerm">
          <div v-if="searchTerm">
            <div v-if="data.didYouMean" class="text-gray-600">
              {{ $texts('did_you_mean', 'Meinten Sie') }}
              <nuxt-link
                :to="{ name: 'search', query: { text: data.didYouMean } }"
                class="font-bold text-primary-500"
              >
                {{ data.didYouMean }} </nuxt-link
              >?
            </div>
            <div v-if="data.total" class="text-gray-600">
              {{
                $textsPlural(
                  'result_count',
                  data.total,
                  '1 Resultat gefunden.',
                  '@count Resultate gefunden.',
                )
              }}
            </div>
            <div v-else class="text-gray-600">
              {{ $texts('no_results', 'Keine Resultate gefunden.') }}
            </div>
            <ol class="grid divide-y divide-gray-300">
              <SearchResult
                v-for="hit in data.hits"
                v-bind="hit"
                :key="hit.id"
              />
            </ol>

            <Pagination
              v-if="totalPages > 1"
              :page="page"
              :total-pages="totalPages"
              class="mt-6 lg:mt-6"
            /></div
        ></Container>
      </div>
      <div class="py-10">
        <BlokkliField :list="node.paragraphs" name="field_paragraphs" />
      </div>
    </main>
  </BlokkliProvider>
</template>

<script lang="ts" setup>
import type { NodePageFragment } from '#graphql-operations'

definePageMeta({
  name: 'search',
  hideBreadcrumb: true,
  drupalFrontendRoute: true,
  languageMapping: {
    de: '/de/suche',
    fr: '/fr/recherche',
    en: '/en/search',
  },
})

const nuxtRoute = useRoute()
const searchTerm = useQueryString('text')
const pageQuery = useQueryString('page', '0')
const bundle = useQueryString('bundle')

const size = 10

const { $texts, $textsPlural } = useEasyTexts()

const page = computed(() => {
  const v = parseInt(pageQuery.value)

  if (v && !isNaN(v) && v >= 0) {
    return v
  }

  return 0
})

// Get the data.
const { data: query } = await useAsyncGraphqlQuery(
  'route',
  { path: nuxtRoute.path },
  {
    transform: function (data) {
      return data.data
    },
    graphqlCaching: {
      client: true,
    },
  },
)

const { data } = await useAsyncData(
  () => {
    if (!searchTerm.value) {
      return Promise.resolve(null)
    }
    return useGraphqlQuery(
      'search',
      {
        text: searchTerm.value,
        page: page.value,
        bundle: bundle.value,
        size,
      },
      {
        graphqlCaching: {
          client: true,
        },
      },
    ).then((v) => v.data.searchContent)
  },
  {
    watch: [page, searchTerm, bundle],
  },
)

const totalPages = computed(() => {
  const total = data?.value?.total ?? 0
  return Math.ceil(total / size)
})

// Handles redirects and metatags.
const { entity: node } = await useDrupalRoute<NodePageFragment>(query.value, {
  noError: true,
})
const breadcrumb = useBreadcrumbLinks()
setBreadcrumbLinksFromRoute(query.value)
setLanguageLinksFromRoute(query.value)
setBusinessAreaFromRoute(query.value)
await renderPageDependencies()
</script>
